import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DocumentPlusIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { maintenanceContractCustomerLabel, type MaintenanceContract } from "@/lib/wartungen";
import {
  CONTRACT_STATUS_LABELS,
  contractDaysUntilEnd,
  contractMatchesQuery,
  contractTitle,
  FILTER_ALL,
  normalizeContractStatus,
  resolveCustomer,
  type ContractStatusKey,
} from "../lib/campaignView";
import { ContractCard } from "./ContractCard";
import { ChipRow, KpiTile } from "./OverviewPrimitives";

type Focus = "all" | "active" | "ending" | "missing_document" | "incomplete";
type Grouping = "customer" | "none";

const ENDING_SOON_DAYS = 90;

/** Vertrag ohne Ort oder ohne Kategorie – damit lässt sich keine Wartung anlegen. */
function contractIncomplete(c: MaintenanceContract): boolean {
  const locations = Array.isArray(c.locations) ? c.locations.length : 0;
  const types = Array.isArray(c.maintenance_types) ? c.maintenance_types.length : 0;
  return locations === 0 || types === 0;
}

function contractEndingSoon(c: MaintenanceContract): boolean {
  if (c.auto_renew_enabled) return false;
  const status = normalizeContractStatus(c.status);
  if (status === "ended" || status === "cancelled") return false;
  const days = contractDaysUntilEnd(c);
  return days != null && days <= ENDING_SOON_DAYS;
}

function matchesFocus(c: MaintenanceContract, focus: Focus): boolean {
  switch (focus) {
    case "active":
      return normalizeContractStatus(c.status) === "active";
    case "ending":
      return contractEndingSoon(c);
    case "missing_document":
      return !c.document;
    case "incomplete":
      return contractIncomplete(c);
    default:
      return true;
  }
}

const STATUS_ORDER: Record<ContractStatusKey, number> = { active: 0, draft: 1, paused: 2, ended: 3, cancelled: 4 };

function compareContracts(a: MaintenanceContract, b: MaintenanceContract): number {
  const sa = STATUS_ORDER[normalizeContractStatus(a.status)];
  const sb = STATUS_ORDER[normalizeContractStatus(b.status)];
  if (sa !== sb) return sa - sb;
  return contractTitle(a).localeCompare(contractTitle(b), "de", { sensitivity: "base", numeric: true });
}

export interface VertraegeTabProps {
  contracts: MaintenanceContract[];
  campaignCountByContract: Map<number, number>;
  onCreateMaintenance: (contract: MaintenanceContract) => void;
  /** `on_call`: gleiche Liste, ohne Kennzahlen zu Laufzeitende und fehlendem PDF. */
  variant?: "contract" | "on_call";
}

/**
 * Tab „Verträge“ in der Wartungsübersicht: Kennzahlen als Schnellfilter,
 * Suche, Statusfilter und klickbare Vertragskacheln – wahlweise nach Kunde gruppiert.
 */
export function VertraegeTab({
  contracts,
  campaignCountByContract,
  onCreateMaintenance,
  variant = "contract",
}: VertraegeTabProps) {
  const onCall = variant === "on_call";
  const nounOne = onCall ? "Eintrag" : "Vertrag";
  const nounMany = onCall ? "Einträge" : "Verträge";
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<ContractStatusKey | typeof FILTER_ALL>(FILTER_ALL);
  const [focus, setFocus] = useState<Focus>("all");
  const [grouping, setGrouping] = useState<Grouping>("customer");

  const kpis = useMemo(
    () => ({
      total: contracts.length,
      active: contracts.filter((c) => normalizeContractStatus(c.status) === "active").length,
      ending: contracts.filter(contractEndingSoon).length,
      missingDocument: contracts.filter((c) => !c.document).length,
      incomplete: contracts.filter(contractIncomplete).length,
    }),
    [contracts],
  );

  const statusOptions = useMemo(() => {
    const order: ContractStatusKey[] = ["active", "draft", "paused", "ended", "cancelled"];
    return order
      .map((key) => ({
        key,
        label: CONTRACT_STATUS_LABELS[key],
        count: contracts.filter((c) => normalizeContractStatus(c.status) === key).length,
      }))
      .filter((o) => o.count > 0);
  }, [contracts]);

  const filtered = useMemo(
    () =>
      contracts
        .filter(
          (c) =>
            (status === FILTER_ALL || normalizeContractStatus(c.status) === status) &&
            matchesFocus(c, focus) &&
            contractMatchesQuery(c, query),
        )
        .sort(compareContracts),
    [contracts, status, focus, query],
  );

  const groups = useMemo(() => {
    if (grouping === "none") return [{ key: "all", label: "", items: filtered }];
    const map = new Map<string, { key: string; label: string; items: MaintenanceContract[] }>();
    for (const c of filtered) {
      const customer = resolveCustomer(c);
      const entry = map.get(customer.key) ?? {
        key: customer.key,
        label: maintenanceContractCustomerLabel(c) || customer.label,
        items: [],
      };
      entry.items.push(c);
      map.set(customer.key, entry);
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "de", { sensitivity: "base" }));
  }, [filtered, grouping]);

  const activeFilterCount = (status !== FILTER_ALL ? 1 : 0) + (focus !== "all" ? 1 : 0) + (query.trim() ? 1 : 0);

  function resetFilters() {
    setQuery("");
    setStatus(FILTER_ALL);
    setFocus("all");
  }

  function toggleFocus(next: Focus) {
    setFocus((prev) => (prev === next ? "all" : next));
  }

  return (
    <>
      <section className={onCall ? "grid gap-3 sm:grid-cols-2" : "grid gap-3 sm:grid-cols-2 lg:grid-cols-4"}>
        <KpiTile
          label={onCall ? "Aktiv" : "Aktive Verträge"}
          value={kpis.active}
          hint={`${kpis.total} ${kpis.total === 1 ? nounOne : nounMany} insgesamt`}
          tone="green"
          active={focus === "active"}
          onClick={() => toggleFocus("active")}
        />
        {onCall ? null : (
          <KpiTile
            label="Laufen bald aus"
            value={kpis.ending}
            hint={`Enden in den nächsten ${ENDING_SOON_DAYS} Tagen ohne automatische Verlängerung`}
            tone={kpis.ending > 0 ? "orange" : "neutral"}
            active={focus === "ending"}
            onClick={() => toggleFocus("ending")}
          />
        )}
        {onCall ? null : (
          <KpiTile
            label="Ohne Vertrags-PDF"
            value={kpis.missingDocument}
            hint="Noch kein Vertragsdokument im InfoHub hinterlegt"
            tone={kpis.missingDocument > 0 ? "accent" : "neutral"}
            active={focus === "missing_document"}
            onClick={() => toggleFocus("missing_document")}
          />
        )}
        <KpiTile
          label="Unvollständig"
          value={kpis.incomplete}
          hint="Ohne Ort oder Kategorie – es kann keine Wartung angelegt werden"
          tone={kpis.incomplete > 0 ? "red" : "neutral"}
          active={focus === "incomplete"}
          onClick={() => toggleFocus("incomplete")}
        />
      </section>

      <section className="space-y-3 rounded-ios border border-separator bg-card p-4 shadow-ios">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[14rem] flex-1">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
              aria-hidden
            />
            <input
              type="search"
              className="eh-input pl-9"
              placeholder={onCall ? "Suchen: Name, Kunde, Ort, Kategorie …" : "Suchen: Name, Vertragsnummer, Kunde, Ort, Kategorie …"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={onCall ? "Einträge auf Zuruf durchsuchen" : "Verträge durchsuchen"}
            />
          </div>
          <SegmentedControl<Grouping>
            name="Gruppierung"
            value={grouping}
            onChange={setGrouping}
            options={[
              { value: "customer", label: "Nach Kunde" },
              { value: "none", label: "Alle" },
            ]}
          />
          {activeFilterCount > 0 ? (
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-sm text-label-secondary hover:text-label"
              onClick={resetFilters}
            >
              <XMarkIcon className="h-4 w-4" aria-hidden />
              Zurücksetzen
            </button>
          ) : null}
        </div>
        {statusOptions.length > 1 ? (
          <div className="border-t border-separator pt-3">
            <ChipRow label="Status" options={statusOptions} selected={status} onSelect={setStatus} />
          </div>
        ) : null}
      </section>

      <p className="text-sm text-label-secondary" aria-live="polite">
        {filtered.length === contracts.length
          ? `${contracts.length} ${contracts.length === 1 ? nounOne : nounMany}`
          : `${filtered.length} von ${contracts.length} ${nounMany}`}
        {grouping === "customer" && groups.length > 1 ? ` · ${groups.length} Kunden` : ""}
      </p>

      {filtered.length === 0 ? (
        <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center shadow-ios">
          <p className="text-sm font-medium text-label">
            {onCall ? "Keine Einträge auf Zuruf für diese Auswahl." : "Keine Verträge für diese Auswahl."}
          </p>
          <p className="mt-1 text-sm text-label-secondary">
            {onCall
              ? "Passe Suche oder Filter an, oder lege einen Eintrag für Wartungen ohne Vertrag an."
              : "Passe Suche oder Filter an, oder lege einen neuen Vertrag an."}
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {activeFilterCount > 0 ? (
              <Button type="button" variant="secondary" onClick={resetFilters}>
                Filter zurücksetzen
              </Button>
            ) : null}
            <Link
              to={onCall ? "/wartungen/vertrag-form?basis=on_call" : "/wartungen/vertrag-form"}
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-accent px-4 text-sm font-medium text-label-on-accent hover:bg-accent-hover"
            >
              <DocumentPlusIcon className="h-4 w-4" aria-hidden />
              {onCall ? "Auf Zuruf anlegen" : "Neuer Vertrag"}
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.key} aria-label={group.label ? `${nounMany} von ${group.label}` : nounMany}>
              {group.label ? (
                <div className="mb-2 flex items-baseline gap-2">
                  <h2 className="text-sm font-semibold text-label">{group.label}</h2>
                  <span className="text-xs text-label-tertiary">
                    {group.items.length} {group.items.length === 1 ? nounOne : nounMany}
                  </span>
                </div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {group.items.map((c) => (
                  <ContractCard
                    key={c.id}
                    contract={c}
                    campaignCount={campaignCountByContract.get(Number(c.id)) ?? 0}
                    onCreateMaintenance={onCreateMaintenance}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
